import ProgramDiscountCodeModal from '@/components/actions/program/ProgramDiscountCodeModal';
import {
  findPresetId,
  generatePreviewCode,
} from '@/components/actions/program/DiscountCodePatternBuilder';
import { DataListItem, DataListRoot } from '@/components/ui/data-list';
import { EmptyState } from '@/components/ui/empty-state';
import { usePreviewBrandName } from '@/components/actions/program/use-preview-brand-name';
import { useAuth } from '@/contexts/auth-context';
import { useIsLocalDiscountMode } from '@/hooks/use-is-local-discount-mode';
import { Badge, Button, Card, Code, Flex, HStack, SimpleGrid, Text } from '@chakra-ui/react';
import { DEFAULT_CODE_PATTERN, type Program } from '@fluenceur/types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditPencil, InfoCircle } from 'iconoir-react';

interface DiscountCodeConfigurationProps {
  program: Program;
}

const DiscountCodeConfiguration: React.FC<DiscountCodeConfigurationProps> = ({ program }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const previewBrandName = usePreviewBrandName();
  const [isModalFormOpen, setIsModalFormOpen] = useState(false);

  const { discountSettings } = program;
  const hasDiscountEnabled = discountSettings?.enabled;

  // Get currency from user's current brand, default to EUR
  const currency = user?.currentBrand?.currency || 'EUR';

  // Local discount code brands (internal/partner) have no Shopify-executed
  // min purchase / auto-apply / once-per-customer settings - hide the rows.
  const isLocalDiscountMode = useIsLocalDiscountMode();

  // Format currency amount
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(t('common.locale'), {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  return (
    <>
      <Card.Root w="full" p={8}>
        <Card.Header p={0}>
          <Flex justifyContent="space-between" alignItems="baseline">
            <Card.Title fontSize="2xl">
              {t('programs.programOverview.discountCode.title')}
            </Card.Title>
            <Button
              variant="outline"
              w="fit-content"
              size="sm"
              onClick={() => setIsModalFormOpen(true)}
            >
              <EditPencil width={20} height={20} />
              {hasDiscountEnabled
                ? t('programs.programOverview.discountCode.edit')
                : t('programs.programOverview.discountCode.add')}
            </Button>
          </Flex>
        </Card.Header>
        <Card.Body p={0} mt={8}>
          {hasDiscountEnabled ? (
            <DataListRoot orientation="vertical" gap={6}>
              <SimpleGrid
                columns={[1, null, 3]}
                gap={6}
                templateColumns={{ base: '1fr', md: '1fr 1fr' }}
              >
                <DataListItem
                  label={t('programs.programOverview.discountCode.discountType')}
                  alignItems="baseline"
                  value={
                    <Text fontWeight="medium" fontSize="md">
                      {discountSettings.type === 'percentage'
                        ? t('programs.programOverview.discountCode.percentage')
                        : t('programs.programOverview.discountCode.fixedAmount')}
                    </Text>
                  }
                />
                <DataListItem
                  label={t('programs.programOverview.discountCode.discountValue')}
                  alignItems="baseline"
                  value={
                    <HStack gap={2}>
                      <Text fontSize="md" fontWeight="medium">
                        {discountSettings.type === 'percentage'
                          ? `${discountSettings.value}%`
                          : formatCurrency(discountSettings.value)}
                      </Text>
                      {!isLocalDiscountMode && discountSettings.minPurchaseAmount && (
                        <Badge colorPalette="blue" variant="subtle" size="sm">
                          {t('programs.programOverview.discountCode.minPurchase')}:{' '}
                          {formatCurrency(discountSettings.minPurchaseAmount)}
                        </Badge>
                      )}
                    </HStack>
                  }
                />
                {!isLocalDiscountMode && (
                  <>
                    <DataListItem
                      label={t('programs.programOverview.discountCode.autoApplyDiscount')}
                      alignItems="baseline"
                      value={
                        <Badge
                          colorPalette={discountSettings.autoApplyDiscount ? 'green' : 'gray'}
                          variant="subtle"
                          size="sm"
                        >
                          {discountSettings.autoApplyDiscount
                            ? t('common.enabled')
                            : t('common.disabled')}
                        </Badge>
                      }
                    />
                    <DataListItem
                      label={t('programs.programOverview.discountCode.oncePerCustomer')}
                      alignItems="baseline"
                      value={
                        <Badge
                          colorPalette={
                            (discountSettings.oncePerCustomer ?? true) ? 'green' : 'gray'
                          }
                          variant="subtle"
                          size="sm"
                        >
                          {(discountSettings.oncePerCustomer ?? true)
                            ? t('common.enabled')
                            : t('common.disabled')}
                        </Badge>
                      }
                    />
                  </>
                )}
                <DataListItem
                  label={t('programs.programOverview.discountCode.codeFormat')}
                  alignItems="baseline"
                  value={
                    <HStack gap={3} align="center">
                      <Code fontSize="sm" fontFamily="mono" fontWeight="semibold" px={2} py={1}>
                        {generatePreviewCode(discountSettings.codePattern ?? DEFAULT_CODE_PATTERN, {
                          brandName: previewBrandName,
                          discountValue: discountSettings.value,
                        })}
                      </Code>
                      <Text fontSize="sm" color="fg.muted">
                        {t(
                          `programs.modals.editDiscountCode.preset_${findPresetId(discountSettings.codePattern ?? DEFAULT_CODE_PATTERN)}`
                        )}
                      </Text>
                    </HStack>
                  }
                />
              </SimpleGrid>
            </DataListRoot>
          ) : (
            <EmptyState
              title={t('programs.programOverview.discountCode.noDiscountTitle')}
              description={t('programs.programOverview.discountCode.noDiscountDesc')}
              icon={<InfoCircle />}
              py={6}
            />
          )}
        </Card.Body>
      </Card.Root>
      <ProgramDiscountCodeModal
        programId={program.id}
        isOpen={isModalFormOpen}
        onClose={() => setIsModalFormOpen(false)}
        defaultValues={{
          discountType: discountSettings?.type,
          discountValue: discountSettings?.value,
          applyDiscount: discountSettings?.enabled,
          minPurchaseAmount: discountSettings?.minPurchaseAmount,
          autoApplyDiscount: discountSettings?.autoApplyDiscount,
          oncePerCustomer: discountSettings?.oncePerCustomer ?? true,
          codePattern: discountSettings?.codePattern,
        }}
      />
    </>
  );
};

export default DiscountCodeConfiguration;
